/* global gsap */
/**
 * Infinite horizontal logo marquee.
 * Requires window.gsap (loaded via snippets/ct-custom-theme.liquid).
 * Must be included from sections/ct-logos.liquid — it is not loaded globally.
 */
if (!customElements.get('ct-logos')) {
  customElements.define(
    'ct-logos',
    class CTLogos extends HTMLElement {
      /** @type {gsap.core.Timeline | null} */
      #loop = null;

      /** @type {ReturnType<typeof setTimeout> | null} */
      #retryTimer = null;

      connectedCallback() {
        this.#initWhenReady();
      }

      disconnectedCallback() {
        if (this.#retryTimer) {
          clearTimeout(this.#retryTimer);
          this.#retryTimer = null;
        }

        if (this.#loop) {
          this.#loop.kill();
          this.#loop = null;
        }
      }

      async #initWhenReady() {
        const gsapLib = await this.#waitForGsap();
        if (!gsapLib || !this.isConnected) return;

        await this.#waitForImages();
        if (!this.isConnected) return;

        const boxes = gsapLib.utils.toArray(this.querySelectorAll('.ct-logo'));
        if (!boxes.length) return;

        // Recreate if theme editor re-renders the section
        if (this.#loop) {
          this.#loop.kill();
          this.#loop = null;
        }

        this.#loop = this.horizontalLoop(boxes, {
          paused: true,
          repeat: -1,
          speed: 0.5,
        });
        this.#loop.play();
      }

      /**
       * @param {number} [maxRetries]
       * @returns {Promise<typeof gsap | null>}
       */
      #waitForGsap(maxRetries = 50) {
        return new Promise((resolve) => {
          let retries = 0;

          const check = () => {
            const w = /** @type {any} */ (window);
            if (w.gsap) {
              resolve(w.gsap);
              return;
            }

            retries += 1;
            if (retries > maxRetries) {
              console.error('CTLogos: GSAP not loaded; logo marquee will not animate.');
              resolve(null);
              return;
            }

            this.#retryTimer = setTimeout(check, 100);
          };

          check();
        });
      }

      /**
       * Lazy/ unloaded images report width 0, which breaks horizontalLoop math.
       * @returns {Promise<void>}
       */
      #waitForImages() {
        const images = Array.from(this.querySelectorAll('img'));
        if (!images.length) return Promise.resolve();

        return Promise.all(
          images.map(
            (img) =>
              new Promise((resolve) => {
                if (img.complete && img.naturalWidth > 0) {
                  resolve(undefined);
                  return;
                }

                const done = () => resolve(undefined);
                img.addEventListener('load', done, { once: true });
                img.addEventListener('error', done, { once: true });
              })
          )
        ).then(() => undefined);
      }

      /*
        GSAP helper: seamless responsive horizontal loop.
        Source pattern: GreenSock forums / utilities.
      */
      horizontalLoop(items, config) {
        const gsapLib = /** @type {any} */ (window).gsap;
        items = gsapLib.utils.toArray(items);
        config = config || {};
        let tl = gsapLib.timeline({
            repeat: config.repeat,
            paused: config.paused,
            defaults: { ease: 'none' },
            onReverseComplete: () => tl.totalTime(tl.rawTime() + tl.duration() * 100),
          }),
          length = items.length,
          startX = items[0].offsetLeft,
          times = [],
          widths = [],
          xPercents = [],
          curIndex = 0,
          pixelsPerSecond = (config.speed || 1) * 100,
          snap = config.snap === false ? (v) => v : gsapLib.utils.snap(config.snap || 1),
          totalWidth,
          curX,
          distanceToStart,
          distanceToLoop,
          item,
          i;

        gsapLib.set(items, {
          xPercent: (i, el) => {
            let w = (widths[i] = parseFloat(gsapLib.getProperty(el, 'width', 'px')));
            xPercents[i] = snap(
              (parseFloat(gsapLib.getProperty(el, 'x', 'px')) / w) * 100 + gsapLib.getProperty(el, 'xPercent')
            );
            return xPercents[i];
          },
        });
        gsapLib.set(items, { x: 0 });
        totalWidth =
          items[length - 1].offsetLeft +
          (xPercents[length - 1] / 100) * widths[length - 1] -
          startX +
          items[length - 1].offsetWidth * gsapLib.getProperty(items[length - 1], 'scaleX') +
          (parseFloat(config.paddingRight) || 0);

        for (i = 0; i < length; i++) {
          item = items[i];
          curX = (xPercents[i] / 100) * widths[i];
          distanceToStart = item.offsetLeft + curX - startX;
          distanceToLoop = distanceToStart + widths[i] * gsapLib.getProperty(item, 'scaleX');
          tl.to(
            item,
            {
              xPercent: snap(((curX - distanceToLoop) / widths[i]) * 100),
              duration: distanceToLoop / pixelsPerSecond,
            },
            0
          )
            .fromTo(
              item,
              { xPercent: snap(((curX - distanceToLoop + totalWidth) / widths[i]) * 100) },
              {
                xPercent: xPercents[i],
                duration: (curX - distanceToLoop + totalWidth - curX) / pixelsPerSecond,
                immediateRender: false,
              },
              distanceToLoop / pixelsPerSecond
            )
            .add('label' + i, distanceToStart / pixelsPerSecond);
          times[i] = distanceToStart / pixelsPerSecond;
        }

        function toIndex(index, vars) {
          vars = vars || {};
          Math.abs(index - curIndex) > length / 2 && (index += index > curIndex ? -length : length);
          let newIndex = gsapLib.utils.wrap(0, length, index),
            time = times[newIndex];
          if (time > tl.time() !== index > curIndex) {
            vars.modifiers = { time: gsapLib.utils.wrap(0, tl.duration()) };
            time += tl.duration() * (index > curIndex ? 1 : -1);
          }
          curIndex = newIndex;
          vars.overwrite = true;
          return tl.tweenTo(time, vars);
        }

        tl.next = (vars) => toIndex(curIndex + 1, vars);
        tl.previous = (vars) => toIndex(curIndex - 1, vars);
        tl.current = () => curIndex;
        tl.toIndex = (index, vars) => toIndex(index, vars);
        tl.times = times;
        tl.progress(1, true).progress(0, true);
        if (config.reversed) {
          tl.vars.onReverseComplete();
          tl.reverse();
        }
        return tl;
      }
    }
  );
}
